import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);

    const finalVariant = this.#resolveVariantForChange(event.target);
    if (!finalVariant) return;

    this.#applyVariantChange(event.target, finalVariant);
  }

  /**
   * Runs the normal variant update flow from a finalized control state.
   * @param {HTMLElement} target
   * @param {Record<string, any>} selectedVariant
   */
  #applyVariantChange(target, selectedVariant) {
    const selectedOption = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;

    if (!selectedOption) return;

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !target.closest('product-card') &&
      !target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption, null, [], selectedVariant), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedVariant?.id || selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Resolve the final variant for the current change and sync all controls to that variant.
   * @param {HTMLElement} target
   * @returns {Record<string, any> | undefined}
   */
  #resolveVariantForChange(target) {
    const changedOptionIndex = this.#getChangedOptionIndex(target);
    if (changedOptionIndex === -1) return this.#selectedVariant;

    const selectedValues = this.#selectedOptionValuesByPosition;
    const exactVariant = this.#findMatchingVariant(selectedValues);

    if (exactVariant) {
      this.#syncControlsToVariant(exactVariant);
      return exactVariant;
    }

    const fallbackVariant = this.#variantData.find((variant) => {
      if (!variant.available) return false;

      return selectedValues
        .slice(0, changedOptionIndex + 1)
        .every((value, index) => variant[`option${index + 1}`] === value);
    });

    if (!fallbackVariant) return undefined;

    this.#syncControlsToVariant(fallbackVariant);
    return fallbackVariant;
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * @param {HTMLElement} target
   * @returns {number}
   */
  #getChangedOptionIndex(target) {
    if (target instanceof HTMLSelectElement) {
      return this.#optionControls.indexOf(target);
    }

    const fieldset = target.closest('fieldset.variant-option');
    if (!(fieldset instanceof HTMLFieldSetElement)) return -1;

    return this.#optionControls.indexOf(fieldset);
  }

  /**
   * @returns {(HTMLSelectElement | HTMLFieldSetElement)[]}
   */
  get #optionControls() {
    return Array.from(this.querySelectorAll('select.variant-option__select, fieldset.variant-option'));
  }

  /**
   * @returns {string[]}
   */
  get #selectedOptionValuesByPosition() {
    return this.#optionControls.map((control) => {
      if (control instanceof HTMLSelectElement) {
        return control.value;
      }

      const checkedInput = control.querySelector('input:checked');
      return checkedInput instanceof HTMLInputElement ? checkedInput.value : '';
    });
  }

  /**
   * @returns {Array<Record<string, any>>}
   */
  get #variantData() {
    const variantDataElement = this.querySelector('[data-variant-data]');
    if (!(variantDataElement instanceof HTMLScriptElement) || !variantDataElement.textContent) {
      return [];
    }

    try {
      return JSON.parse(variantDataElement.textContent);
    } catch (error) {
      console.error('Unable to parse variant data', error);
      return [];
    }
  }

  /**
   * @returns {Record<string, any> | undefined}
   */
  get #selectedVariant() {
    const selectedValues = this.#selectedOptionValuesByPosition;

    return this.#findMatchingVariant(selectedValues);
  }

  /**
   * @param {string[]} selectedValues
   * @returns {Record<string, any> | undefined}
   */
  #findMatchingVariant(selectedValues) {
    return this.#variantData.find((variant) => {
      if (!variant.available) return false;

      return selectedValues.every((value, index) => variant[`option${index + 1}`] === value);
    });
  }

  /**
   * @param {Record<string, any>} variant
   */
  #syncControlsToVariant(variant) {
    this.#optionControls.forEach((control, index) => {
      const value = variant[`option${index + 1}`];
      if (!value) return;

      if (control instanceof HTMLSelectElement) {
        const option = Array.from(control.options).find((item) => item.value === value);
        if (!option) return;

        control.value = option.value;
        this.updateSelectedOption(control);
        return;
      }

      const input = Array.from(control.querySelectorAll('input')).find((item) => item.value === value);
      if (input instanceof HTMLInputElement) {
        this.updateSelectedOption(input);
      }
    });
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], selectedVariant = this.#selectedVariant) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (selectedVariant?.id) {
      params.push(`variant=${selectedVariant.id}`);
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]:not([data-variant-data])`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
